import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import SearchableSelect from '../components/SearchableSelect';
import Pagination from '../components/Pagination';
import { 
  ChatBubbleLeftRightIcon,
  BuildingOfficeIcon,
  UserIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  DocumentTextIcon,
  CogIcon,
  SignalIcon,
  ExclamationTriangleIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import toast from '../utils/toast';

const AdminWhatsAppAgents = () => {
  const [loading, setLoading] = useState(true);
  const [whatsappAgents, setWhatsappAgents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [organizationFilter, setOrganizationFilter] = useState('all');
  const [organizations, setOrganizations] = useState([]);
  const [expandedAgents, setExpandedAgents] = useState({});
  const [agentDetails, setAgentDetails] = useState({});
  const [loadingDetails, setLoadingDetails] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      
      const [agentsResponse, orgsResponse] = await Promise.all([
        api.getAllWhatsAppAgents(token),
        api.getAllOrganizations(token)
      ]);
      
      setWhatsappAgents(agentsResponse.data.agents);
      setOrganizations(orgsResponse.data.organizations);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load WhatsApp agents data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAgentDetails = async (agentId) => {
    if (agentDetails[agentId] || loadingDetails[agentId]) return;
    
    try {
      setLoadingDetails(prev => ({ ...prev, [agentId]: true }));
      const token = localStorage.getItem('admin_token');
      const response = await api.getAgentDetails(token, agentId);
      setAgentDetails(prev => ({ ...prev, [agentId]: response.data.agent }));
    } catch (error) {
      console.error('Failed to fetch agent details:', error);
      toast.error('Failed to load agent details');
    } finally {
      setLoadingDetails(prev => ({ ...prev, [agentId]: false }));
    }
  };

  const toggleAgentExpansion = (agentId) => {
    const isExpanded = expandedAgents[agentId];
    setExpandedAgents(prev => ({ ...prev, [agentId]: !isExpanded }));
    
    if (!isExpanded) {
      fetchAgentDetails(agentId);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
      case 'WORKING':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircleIcon className="w-3 h-3 mr-1" />
            Active
          </span>
        );
      case 'inactive':
      case 'STOPPED':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircleIcon className="w-3 h-3 mr-1" />
            Inactive
          </span>
        );
      case 'connecting':
      case 'STARTING':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <ClockIcon className="w-3 h-3 mr-1" />
            Connecting
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
            Unknown
          </span>
        );
    }
  };

  const getStatusIndicator = (status) => {
    switch (status) {
      case 'active':
      case 'WORKING':
        return <span className="w-3 h-3 bg-green-500 rounded-full"></span>;
      case 'inactive':
      case 'STOPPED':
        return <span className="w-3 h-3 bg-red-500 rounded-full"></span>;
      case 'connecting':
      case 'STARTING':
        return <span className="w-3 h-3 bg-yellow-500 rounded-full animate-pulse"></span>;
      default:
        return <span className="w-3 h-3 bg-gray-400 rounded-full"></span>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString() + ' ' + new Date(dateString).toLocaleTimeString();
  };

  const viewKnowledgeBaseDocument = (agentId, documentId, filename) => {
    const token = localStorage.getItem('admin_token');
    const url = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081'}/api/admin/agents/${agentId}/documents/${documentId}/download?token=${token}`;
    window.open(url, '_blank');
  };

  const downloadKnowledgeBaseDocument = (agentId, documentId, filename) => {
    const token = localStorage.getItem('admin_token');
    const url = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081'}/api/admin/agents/${agentId}/documents/${documentId}/download?token=${token}`;
    
    // Create a temporary link to trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`Downloading ${filename}...`);
  };

  const filteredAgents = whatsappAgents.filter(agent => {
    const matchesSearch = agent.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         agent.whatsapp_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         agent.organization_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         agent.client_id?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || agent.status === statusFilter;
    const matchesOrganization = organizationFilter === 'all' || agent.organization_id === organizationFilter;
    
    return matchesSearch && matchesStatus && matchesOrganization;
  });

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, organizationFilter]);

  // Paginated data
  const paginatedAgents = filteredAgents.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="bg-gray-50 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-whatsapp-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading WhatsApp agents...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center">
                <ChatBubbleLeftRightIcon className="h-8 w-8 text-whatsapp-600 mr-3" />
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">WhatsApp Agents</h1>
                  <p className="mt-1 text-sm text-gray-500">
                    Manage all WhatsApp agents and their AI assistants
                  </p>
                </div>
              </div>
              <div className="mt-4 sm:mt-0">
                <div className="flex items-center space-x-4">
                  <div className="text-sm text-gray-500">
                    Total: <span className="font-semibold text-gray-900">{filteredAgents.length}</span> agents
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                    <span className="text-sm text-gray-600">
                      {filteredAgents.filter(a => a.status === 'active' || a.status === 'WORKING').length} Active
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Filters */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Search */}
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search agents..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-whatsapp-500 focus:border-transparent"
                />
              </div>

              {/* Status Filter */}
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 z-10 pointer-events-none">
                  <FunnelIcon className="h-5 w-5" />
                </div>
                <div className="pl-8">
                  <SearchableSelect
                    options={[
                      { value: 'all', label: 'All Statuses', icon: <FunnelIcon className="h-4 w-4" /> },
                      { value: 'active', label: 'Active', icon: <CheckCircleIcon className="h-4 w-4 text-green-600" /> },
                      { value: 'WORKING', label: 'Working', icon: <RocketLaunchIcon className="h-4 w-4 text-blue-600" /> },
                      { value: 'inactive', label: 'Inactive', icon: <XCircleIcon className="h-4 w-4 text-red-600" /> },
                      { value: 'STOPPED', label: 'Stopped', icon: <XCircleIcon className="h-4 w-4 text-gray-600" /> },
                      { value: 'connecting', label: 'Connecting', icon: <ClockIcon className="h-4 w-4 text-yellow-600" /> },
                      { value: 'STARTING', label: 'Starting', icon: <SignalIcon className="h-4 w-4 text-orange-600" /> }
                    ]}
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    placeholder="Select Status..."
                    isSearchable={false}
                  />
                </div>
              </div>

              {/* Organization Filter */}
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 z-10 pointer-events-none">
                  <BuildingOfficeIcon className="h-5 w-5" />
                </div>
                <div className="pl-8">
                  <SearchableSelect
                    options={[
                      { value: 'all', label: 'All Organizations', icon: <BuildingOfficeIcon className="h-4 w-4" /> },
                      ...organizations.map(org => ({
                        value: org.id,
                        label: org.name,
                        icon: <BuildingOfficeIcon className="h-4 w-4 text-blue-600" />
                      }))
                    ]}
                    value={organizationFilter}
                    onChange={(e) => setOrganizationFilter(e.target.value)}
                    placeholder="Select Organization..."
                    isSearchable={true}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Agents List */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">WhatsApp Agents & AI Assistants</h2>
              <p className="text-sm text-gray-600">Click on agents to view their AI assistant details</p>
            </div>
            
            <div className="divide-y divide-gray-200">
              {filteredAgents.length === 0 ? (
                <div className="px-6 py-8 text-center">
                  <ChatBubbleLeftRightIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-4 text-lg font-medium text-gray-900">No WhatsApp agents found</h3>
                  <p className="mt-2 text-gray-600">Try adjusting your search or filter criteria.</p>
                </div>
              ) : (
                paginatedAgents.map((agent) => (
                  <div key={agent.id} className="px-6 py-4">
                    {/* Agent Header */}
                    <div 
                      className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-3 rounded-lg transition-colors"
                      onClick={() => toggleAgentExpansion(agent.id)}
                    >
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                          {expandedAgents[agent.id] ? (
                            <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                          ) : (
                            <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                          )}
                          {getStatusIndicator(agent.status)}
                        </div>
                        <ChatBubbleLeftRightIcon className="h-8 w-8 text-whatsapp-600" />
                        <div>
                          <div className="flex items-center space-x-3">
                            <p className="font-semibold text-gray-900">{agent.display_name}</p>
                            {getStatusBadge(agent.status)}
                          </div>
                          <div className="flex items-center space-x-4 mt-1 text-sm text-gray-500">
                            <span>{agent.whatsapp_number}</span>
                            <span>•</span>
                            <span className="flex items-center">
                              <BuildingOfficeIcon className="h-4 w-4 mr-1" />
                              {agent.organization_name}
                            </span>
                            <span>•</span>
                            <span>ID: {agent.client_id}</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <div className="text-right">
                            <p className="font-medium text-gray-900">{agent.message_count || 0} messages</p>
                            <p className="text-xs">Created {formatDate(agent.created_at)}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Agent Details */}
                    {expandedAgents[agent.id] && (
                      <div className="ml-12 mt-4 p-4 bg-gray-50 rounded-lg">
                        {loadingDetails[agent.id] ? (
                          <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-whatsapp-600"></div>
                            <span className="ml-3 text-gray-600">Loading agent details...</span>
                          </div>
                        ) : agentDetails[agent.id] ? (
                          <div className="space-y-6">
                            {/* AI Assistant Info */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                              <div className="space-y-4">
                                <div className="flex items-center space-x-2">
                                  <RocketLaunchIcon className="h-5 w-5 text-purple-600" />
                                  <h3 className="text-lg font-semibold text-gray-900">AI Assistant</h3>
                                </div>
                                
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Assistant Name</label>
                                  <p className="text-sm text-gray-900 bg-white p-2 rounded border">
                                    {agentDetails[agent.id].ai_agent?.name || 'Not configured'}
                                  </p>
                                </div>
                                
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                                  <p className="text-sm text-gray-900 bg-white p-2 rounded border">
                                    {agentDetails[agent.id].ai_agent?.model || 'Default'}
                                  </p>
                                </div>
                                
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                  <div className="bg-white p-2 rounded border">
                                    {agentDetails[agent.id].ai_agent ? (
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                        <CheckCircleIcon className="w-3 h-3 mr-1" />
                                        Configured
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                        <XCircleIcon className="w-3 h-3 mr-1" />
                                        Not Configured
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              
                              <div className="space-y-4">
                                <div className="flex items-center space-x-2">
                                  <CogIcon className="h-5 w-5 text-blue-600" />
                                  <h3 className="text-lg font-semibold text-gray-900">Configuration</h3>
                                </div>
                                
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Created By</label>
                                  <div className="flex items-center space-x-2 bg-white p-2 rounded border">
                                    <UserIcon className="h-4 w-4 text-gray-400" />
                                    <span className="text-sm text-gray-900">
                                      {agentDetails[agent.id].creator_name || 'Unknown'}
                                    </span>
                                  </div>
                                </div>
                                
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Activity</label>
                                  <div className="flex items-center space-x-2 bg-white p-2 rounded border">
                                    <SignalIcon className="h-4 w-4 text-gray-400" />
                                    <span className="text-sm text-gray-900">
                                      {formatDate(agent.last_activity)}
                                    </span>
                                  </div>
                                </div>
                                
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Message Count</label>
                                  <div className="flex items-center space-x-2 bg-white p-2 rounded border">
                                    <ChatBubbleLeftRightIcon className="h-4 w-4 text-gray-400" />
                                    <span className="text-sm text-gray-900">
                                      {agent.message_count || 0} messages processed
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Instructions */}
                            {agentDetails[agent.id].ai_agent?.instructions && (
                              <div>
                                <div className="flex items-center space-x-2 mb-3">
                                  <DocumentTextIcon className="h-5 w-5 text-green-600" />
                                  <h3 className="text-lg font-semibold text-gray-900">Agent Instructions</h3>
                                </div>
                                <div className="bg-white p-4 rounded border">
                                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                    {agentDetails[agent.id].ai_agent.instructions}
                                  </p>
                                </div>
                              </div>
                            )}

                            {/* Knowledge Base */}
                            {agentDetails[agent.id].knowledge_base && agentDetails[agent.id].knowledge_base.length > 0 && (
                              <div>
                                <div className="flex items-center space-x-2 mb-3">
                                  <DocumentTextIcon className="h-5 w-5 text-orange-600" />
                                  <h3 className="text-lg font-semibold text-gray-900">Knowledge Base Documents</h3>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                  {agentDetails[agent.id].knowledge_base.map((doc, index) => (
                                    <div key={doc.id || index} className="bg-white p-3 rounded border">
                                      <div className="flex items-center space-x-2 mb-2">
                                        <DocumentTextIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                          <p className="text-sm font-medium text-gray-900 truncate" title={doc.original_filename || doc.filename}>
                                            {doc.original_filename || doc.filename || `Document ${index + 1}`}
                                          </p>
                                          <p className="text-xs text-gray-500">
                                            {doc.size ? `${Math.round(doc.size / 1024)}KB` : 'Unknown size'}
                                            {doc.mime_type && ` • ${doc.mime_type.split('/')[1].toUpperCase()}`}
                                          </p>
                                        </div>
                                      </div>
                                      {doc.id && (
                                        <div className="flex space-x-2">
                                          <button
                                            onClick={() => viewKnowledgeBaseDocument(agentDetails[agent.id].ai_agent.id, doc.id, doc.original_filename || doc.filename)}
                                            className="flex-1 inline-flex items-center justify-center px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded hover:bg-blue-200 transition-colors"
                                          >
                                            <EyeIcon className="h-3 w-3 mr-1" />
                                            View
                                          </button>
                                          <button
                                            onClick={() => downloadKnowledgeBaseDocument(agentDetails[agent.id].ai_agent.id, doc.id, doc.original_filename || doc.filename)}
                                            className="flex-1 inline-flex items-center justify-center px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded hover:bg-green-200 transition-colors"
                                          >
                                            <svg className="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                            Download
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
                            <h3 className="mt-4 text-lg font-medium text-gray-900">No details available</h3>
                            <p className="mt-2 text-gray-600">Unable to load agent details at this time.</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
            
            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalItems={filteredAgents.length}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              showItemCount={true}
            />
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminWhatsAppAgents;
